import { z } from 'zod'

// Section identifiers
export const landingSectionEnum = z.enum([
  'hero',
  'activityCards',
  'valueProps',
  'features',
  'contact',
  'partners',
  'finalCta',
  'footer',
])

export type LandingSection = z.infer<typeof landingSectionEnum>

// Per-section content schemas
export const heroContentSchema = z.object({
  ctaPrimaryText: z.string(),
  ctaPrimaryUrl: z.string(),
  ctaSecondaryText: z.string(),
  ctaSecondaryUrl: z.string(),
  dropPinPromptText: z.string(),
  sidebarHeaderText: z.string(),
  sidebarCtaText: z.string(),
  sidebarCtaUrl: z.string(),
})

export const activityCardsContentSchema = z.object({
  sectionLabel: z.string(),
  heading: z.string(),
  subheading: z.string(),
  cards: z.array(
    z.object({
      tag: z.string(),
      tagColor: z.enum(['blue', 'green', 'purple']),
      title: z.string(),
      description: z.string(),
      date: z.string(),
      imageUrl: z
        .string()
        .url()
        .startsWith('https://')
        .nullable()
        .default(null),
      imageBg: z.string(),
    }),
  ),
})

export const valuePropsContentSchema = z.object({
  sectionLabel: z.string(),
  heading: z.string(),
  subheading: z.string(),
  items: z.array(
    z.object({
      icon: z.string(),
      title: z.string(),
      description: z.string(),
    }),
  ),
})

export const featuresContentSchema = z.object({
  features: z.array(
    z.object({
      sectionLabel: z.string(),
      title: z.string(),
      body: z.string(),
      imageUrl: z.string().url().startsWith('https://').nullable(),
      imageAlt: z.string(),
      imagePosition: z.enum(['left', 'right']),
    }),
  ),
})

export const contactContentSchema = z.object({
  sectionLabel: z.string(),
  heading: z.string(),
  subheading: z.string(),
  submitButtonText: z.string(),
  footnote: z.string(),
})

export const partnersContentSchema = z.object({
  sectionLabel: z.string(),
  heading: z.string(),
  subheading: z.string(),
  partners: z.array(
    z.object({
      name: z.string(),
      logoUrl: z.string().url().startsWith('https://').nullable(),
    }),
  ),
})

export const finalCtaContentSchema = z.object({
  heading: z.string(),
  subheading: z.string(),
  ctaPrimaryText: z.string(),
  ctaPrimaryUrl: z.string(),
  ctaSecondaryText: z.string(),
  ctaSecondaryUrl: z.string(),
})

export const footerContentSchema = z.object({
  brandDescription: z.string(),
  columns: z.array(
    z.object({
      title: z.string(),
      links: z.array(
        z.object({
          label: z.string(),
          url: z.string(),
        }),
      ),
    }),
  ),
  copyrightText: z.string(),
  socialLinks: z.array(
    z.object({
      platform: z.string(),
      url: z.string(),
      label: z.string(),
    }),
  ),
})

// Map section key to its content schema
export const sectionContentSchemas = {
  hero: heroContentSchema,
  activityCards: activityCardsContentSchema,
  valueProps: valuePropsContentSchema,
  features: featuresContentSchema,
  contact: contactContentSchema,
  partners: partnersContentSchema,
  finalCta: finalCtaContentSchema,
  footer: footerContentSchema,
} as const

// Inferred types
export type HeroContent = z.infer<typeof heroContentSchema>
export type ActivityCardsContent = z.infer<typeof activityCardsContentSchema>
export type ValuePropsContent = z.infer<typeof valuePropsContentSchema>
export type FeaturesContent = z.infer<typeof featuresContentSchema>
export type ContactContent = z.infer<typeof contactContentSchema>
export type PartnersContent = z.infer<typeof partnersContentSchema>
export type FinalCtaContent = z.infer<typeof finalCtaContentSchema>
export type FooterContent = z.infer<typeof footerContentSchema>

// Union of all section content types
export type SectionContent =
  | HeroContent
  | ActivityCardsContent
  | ValuePropsContent
  | FeaturesContent
  | ContactContent
  | PartnersContent
  | FinalCtaContent
  | FooterContent

// Consolidated landing content (what's in S3/CDN -- per-page format: { content, publishedAt })
export const landingContentResponseSchema = z.object({
  content: z.object({
    slug: z.string().optional(),
    industryName: z.string().optional(),
    hero: heroContentSchema.nullable(),
    activityCards: activityCardsContentSchema.nullable(),
    valueProps: valuePropsContentSchema.nullable(),
    features: featuresContentSchema.nullable(),
    contact: contactContentSchema.nullable(),
    partners: partnersContentSchema.nullable(),
    finalCta: finalCtaContentSchema.nullable(),
    footer: footerContentSchema.nullable(),
  }),
  publishedAt: z.string().datetime().optional(),
})

export type LandingContentResponse = z.infer<
  typeof landingContentResponseSchema
>

// Landing page index manifest (landing/index.json on CDN)
export const landingIndexSchema = z.object({
  pages: z.array(
    z.object({
      slug: z.string(),
      title: z.string().nullable(),
      industryName: z.string(),
    }),
  ),
  publishedAt: z.string().datetime().optional(),
})

export type LandingIndex = z.infer<typeof landingIndexSchema>

// Admin response includes metadata
export const landingContentAdminSectionSchema = z.object({
  section: landingSectionEnum,
  content: z.unknown(),
  updated_at: z.string().datetime().nullable(),
  updated_by: z.string().nullable(),
  updated_by_name: z.string().nullable().optional(),
})

export type LandingContentAdminSection = z.infer<
  typeof landingContentAdminSectionSchema
>
